import { flow } from '@helo/utils'
import { z } from 'zod'

import { ConfigState } from './configSlice'

type ConfigStateValues = Omit<ConfigState, 'isEnvironment'>

/** Canonical `ENVIRONMENT` values (hosted HTML and local `.env`). */
export const environmentSchema = z.enum([
  'localhost',
  'dev',
  'qa',
  'production',
])
export type ENVIRONMENTS = z.infer<typeof environmentSchema>

/** Shape of `window.appConfig` injected into `index.html` at build or deploy time. */
export const appConfigSchema = z.object({
  AGGRID_LICENSE: z.string().min(1),
  API_URL: z.string().min(1),
  APPINSIGHTS_CONNECTION_STRING: z.string().nullable().optional().default(null),
  AUTH0_CLIENTID: z.string().min(1),
  AUTH0_DOMAIN: z.string().min(1),
  AUTH_AUDIENCE: z.string().min(1),
  ENVIRONMENT: environmentSchema,
  VERSION: z.string().nullable().optional().default(null),
})

export type AppConfig = z.infer<typeof appConfigSchema>

export const environmentsInputToArray = () =>
  flow(flow.inputType<ENVIRONMENTS | ENVIRONMENTS[]>(), environments =>
    Array.isArray(environments) ? environments : [environments],
  )

export const getAppConfig = (): unknown => {
  if (typeof window === 'undefined') return undefined
  return window.appConfig
}

const missingAppConfigMessage = `window.appConfig is missing.

Ensure window.appConfig is injected for this environment:
- Local dev: confirm apps/ui/.env values and the Vite appConfigInjector plugin
- Hosted (dev/qa/production): confirm the Azure Static Web App is injecting the dynamic appConfig into the HTML before serving`

export const validateAppConfig = () =>
  flow(flow.inputType<unknown>(), (raw): ConfigStateValues => {
    if (raw == null) {
      console.error(missingAppConfigMessage)
      throw new Error('window.appConfig is missing.')
    }

    const result = appConfigSchema.safeParse(raw)
    if (!result.success) {
      const message = `window.appConfig failed validation:\n${z.prettifyError(result.error)}`
      console.error(message)
      throw new Error(message)
    }

    return result.data as ConfigStateValues
  })

const createInitialConfigState = (): ConfigStateValues => {
  return {
    VERSION: null,
    AUTH0_DOMAIN: null,
    AUTH0_CLIENTID: null,
    AUTH_AUDIENCE: null,
    API_URL: null,
    ENVIRONMENT: null,
    AGGRID_LICENSE: null,
    APPINSIGHTS_CONNECTION_STRING: null,
  } as unknown as ConfigStateValues
}

export const ConfigSliceParts = {
  appConfigSchema,
  environmentSchema,
  environmentsInputToArray,
  getAppConfig,
  validateAppConfig,
  createInitialConfigState,
}
